/**
 * Resolve University lesson Portable Text hrefs (#lesson:slug, *.md paths) to
 * in-app course lesson routes.
 *
 * Also repairs importer leftovers where bold-wrapped markdown links were stored
 * as literal `[label](url)` text instead of Portable Text link marks.
 */

export type UniversityLessonRef = {
  moduleId: number;
  lessonId: number;
  lessonSlug: string;
  moduleSlug: string;
};

export type UniversityLessonLinkIndex = Map<string, UniversityLessonRef>;

const MARKDOWN_LINK_RE = /\[([^\]]+)\]\(([^)]+)\)/g;

function slugCurrent(slug: unknown): string {
  if (typeof slug === "string") return slug.trim();
  if (slug && typeof slug === "object" && typeof (slug as { current?: unknown }).current === "string") {
    return (slug as { current: string }).current.trim();
  }
  return "";
}

function shortKey(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  }
  return `md${(hash >>> 0).toString(16)}`;
}

/** Build slug → module/lesson numeric IDs from a Sanity course (1-based IDs). */
export function buildUniversityLessonLinkIndex(
  modules: Array<{ slug?: unknown; lessons?: Array<{ slug?: unknown }> | null }> | null | undefined,
): UniversityLessonLinkIndex {
  const index: UniversityLessonLinkIndex = new Map();
  if (!modules?.length) return index;

  modules.forEach((module, moduleIndex) => {
    const moduleSlug = slugCurrent(module.slug);
    const lessons = module.lessons || [];
    lessons.forEach((lesson, lessonIndex) => {
      const lessonSlug = slugCurrent(lesson.slug);
      if (!lessonSlug) return;
      const ref: UniversityLessonRef = {
        moduleId: moduleIndex + 1,
        lessonId: lessonIndex + 1,
        lessonSlug,
        moduleSlug,
      };
      index.set(lessonSlug.toLowerCase(), ref);
    });
  });

  return index;
}

/**
 * Normalize imported markdown / case-study paths into `#lesson:slug` form when possible.
 */
export function canonicalizeUniversityHref(href: string): string {
  const u = (href || "").trim();
  if (!u) return u;

  if (/case-study-bb-btc-bundle\.md/i.test(u)) {
    if (/#cohorts/i.test(u)) return "#lesson:case-study-cohorts";
    if (/#discovery/i.test(u) || /#v57/i.test(u) || /#v63/i.test(u)) {
      return "#lesson:case-study-discovery";
    }
    if (/#live-forward/i.test(u) || /#final-variants/i.test(u)) {
      return "#lesson:case-study-final-and-forward";
    }
    if (/#course-cross-references/i.test(u) || /#validation-discipline/i.test(u)) {
      return "#lesson:case-study-discipline-and-links";
    }
    return "#lesson:case-study-overview";
  }

  return u;
}

/**
 * Extract a lesson slug candidate from an imported markdown / Sanity link href.
 * Returns null for external URLs, assets, and non-lesson hashes.
 */
export function extractLessonSlugFromHref(href: string): string | null {
  const raw = canonicalizeUniversityHref(href);
  if (!raw) return null;

  // Explicit importer rewrite: #lesson:welcome
  const hashLesson = raw.match(/^#lesson:([a-z0-9-]+)$/i);
  if (hashLesson?.[1]) return hashLesson[1].toLowerCase();

  // Already an in-app university lesson URL
  const appPath = raw.match(
    /\/university\/course\/[^/]+\/module\/(\d+)\/lesson\/(\d+)/i,
  );
  if (appPath) return null; // handled separately via parseUniversityLessonPath

  // External / protocol links
  if (/^(https?:|mailto:|tel:)/i.test(raw)) return null;

  // Asset / data files — not lessons
  if (/\.(png|jpe?g|gif|webp|svg|mmd|json|csv|pdf)(\?.*)?$/i.test(raw)) return null;
  if (/\/assets\//i.test(raw)) return null;

  // Same-document anchors that are not #lesson:… (e.g. #cohorts)
  if (raw.startsWith("#")) return null;

  // Bare or relative markdown lesson files: 02-three-questions.md, ../foo/01-welcome.md
  const mdMatch = raw.match(/(?:^|\/)([a-z0-9-]+)\.md(?:#.*)?$/i);
  if (mdMatch?.[1]) {
    const fileSlug = mdMatch[1].replace(/^\d+-/, "").toLowerCase();
    return fileSlug || null;
  }

  // Directory-style module refs are not a single lesson
  if (raw.endsWith("/")) return null;

  return null;
}

export function parseUniversityLessonPath(
  href: string,
): { courseId: string; moduleId: number; lessonId: number } | null {
  const match = (href || "").trim().match(
    /\/university\/course\/([^/]+)\/module\/(\d+)\/lesson\/(\d+)/i,
  );
  if (!match) return null;
  return {
    courseId: match[1],
    moduleId: parseInt(match[2], 10),
    lessonId: parseInt(match[3], 10),
  };
}

export function resolveUniversityLessonHref(
  href: string,
  index: UniversityLessonLinkIndex,
  courseId: string,
): string | null {
  const trimmed = canonicalizeUniversityHref(href);
  if (!trimmed || !courseId) return null;

  const existing = parseUniversityLessonPath(trimmed);
  if (existing) {
    // Prefer staying in the current course when the path is relative-ish to this course
    const id = existing.courseId === courseId ? courseId : existing.courseId;
    return `/university/course/${id}/module/${existing.moduleId}/lesson/${existing.lessonId}`;
  }

  const slug = extractLessonSlugFromHref(trimmed);
  if (!slug) return null;

  const ref = index.get(slug);
  if (!ref) return null;

  return `/university/course/${courseId}/module/${ref.moduleId}/lesson/${ref.lessonId}`;
}

/** True when the href looks like an intended in-course lesson link (even if unresolved). */
export function isUniversityLessonHref(href: string): boolean {
  const trimmed = canonicalizeUniversityHref(href);
  if (!trimmed) return false;
  if (parseUniversityLessonPath(trimmed)) return true;
  if (/^#lesson:/i.test(trimmed)) return true;
  if (/\.md(?:#.*)?$/i.test(trimmed) && !/\/assets\//i.test(trimmed)) return true;
  return false;
}

type PtSpan = {
  _type?: string;
  _key?: string;
  text?: string;
  marks?: string[];
};

type PtMarkDef = {
  _type?: string;
  _key?: string;
  href?: string;
  [key: string]: unknown;
};

type PtBlock = {
  _type?: string;
  _key?: string;
  children?: PtSpan[];
  markDefs?: PtMarkDef[];
  [key: string]: unknown;
};

/**
 * Convert leftover markdown links inside span text into real Portable Text link marks.
 * Fixes bold-wrapped imports like `**[Cohort B](../../case-study-bb-btc-bundle.md#cohorts)**`.
 */
export function normalizePortableTextMarkdownLinks(content: unknown): any[] | null | undefined {
  if (!content || !Array.isArray(content)) return content as any[] | null | undefined;

  return content.map((block, blockIndex) => {
    if (!block || typeof block !== "object") return block;
    const typed = block as PtBlock;
    if (typed._type !== "block" || !Array.isArray(typed.children)) return block;

    const markDefs: PtMarkDef[] = Array.isArray(typed.markDefs) ? [...typed.markDefs] : [];
    const nextChildren: PtSpan[] = [];
    let changed = false;

    typed.children.forEach((child, childIndex) => {
      if (!child || typeof child !== "object" || typeof child.text !== "string") {
        nextChildren.push(child);
        return;
      }

      const text = child.text;
      if (!text.includes("](")) {
        nextChildren.push(child);
        return;
      }

      MARKDOWN_LINK_RE.lastIndex = 0;
      const matches: RegExpExecArray[] = [];
      let linkMatch: RegExpExecArray | null = MARKDOWN_LINK_RE.exec(text);
      while (linkMatch) {
        matches.push(linkMatch);
        linkMatch = MARKDOWN_LINK_RE.exec(text);
      }
      if (!matches.length) {
        nextChildren.push(child);
        return;
      }

      changed = true;
      const existingMarks = Array.isArray(child.marks) ? [...child.marks] : [];
      let cursor = 0;

      matches.forEach((match, matchIndex) => {
        const full = match[0];
        const label = match[1] || "";
        const href = canonicalizeUniversityHref(match[2] || "");
        const start = match.index ?? 0;

        if (start > cursor) {
          nextChildren.push({
            _type: "span",
            _key: `${child._key || `c${childIndex}`}-t${matchIndex}a`,
            text: text.slice(cursor, start),
            marks: existingMarks,
          });
        }

        const linkKey = shortKey(`${blockIndex}:${childIndex}:${matchIndex}:${href}:${label}`);
        markDefs.push({ _type: "link", _key: linkKey, href });
        nextChildren.push({
          _type: "span",
          _key: `${child._key || `c${childIndex}`}-l${matchIndex}`,
          text: label,
          marks: [...existingMarks, linkKey],
        });

        cursor = start + full.length;
      });

      if (cursor < text.length) {
        nextChildren.push({
          _type: "span",
          _key: `${child._key || `c${childIndex}`}-tail`,
          text: text.slice(cursor),
          marks: existingMarks,
        });
      }
    });

    if (!changed) return block;
    return {
      ...typed,
      children: nextChildren,
      markDefs,
    };
  });
}
